import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Card,
  Input,
  Space,
  Popconfirm,
  Typography,
  Spin,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { useNavigate } from 'react-router';

const { Option } = Select;

const ContactManagement: React.FC = observer(() => {
  const { t } = useTranslation();
  const { applicationStore, crmStore } = useStore();
  const navigate = useNavigate();

  /* ───────────────────────────────────── filters ─────────────────────────────────── */

  const [search, setSearch] = useState<string>('');
  const [filterRole, setFilterRole] = useState<number | null>(null);
  const [filterCompany, setFilterCompany] = useState<number | null>(null);

  const resetFilters = () => {
    setSearch('');
    setFilterRole(null);
    setFilterCompany(null);
  };

  const filteredContacts = useMemo(() => {
    return crmStore.contacts.filter((c) => {
      const q = search.toLowerCase();
      const matchesSearch = q
        ? [c.name, c.surname, c.email, c.phoneNumber]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(q))
        : true;
      const matchesRole = filterRole ? c.roleId === filterRole : true;
      const matchesCompany = filterCompany ? c.externalCompanyId === filterCompany : true;
      return matchesSearch && matchesRole && matchesCompany;
    });
  }, [crmStore.contacts, search, filterRole, filterCompany]);

  /* ───────────────────────────────────── data loading ───────────────────────────── */

  useEffect(() => {
    Promise.all([
      crmStore.getContacts(),
      crmStore.getClientRoles(),
      crmStore.getExternalCompanies(),
    ]);
  }, [crmStore]);

  /* ───────────────────────────────────── columns ─────────────────────────────────── */

  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('user.Name'), dataIndex: 'name', key: 'name', width: '15%' },
    { title: t('Surname'), dataIndex: 'surname', key: 'surname', width: '15%' },
    { title: t('Email'), dataIndex: 'email', key: 'email', width: '20%' },
    { title: t('Phone'), dataIndex: 'phoneNumber', key: 'phoneNumber', width: '15%' },
    {
      title: t('Role'),
      dataIndex: 'roleId',
      key: 'roleId',
      width: '10%',
      render: (roleId: number) => crmStore.clientRoles.find((r) => r.id === roleId)?.name || '',
    },
    {
      title: t('Company'),
      dataIndex: 'externalCompanyId',
      key: 'externalCompanyId',
      width: '15%',
      render: (companyId: number) =>
        crmStore.externalCompanies.find((c) => c.id === companyId)?.name || '',
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}              /* opens DETAILS now */
            onClick={() =>
              navigate(`/${applicationStore.company?.Tenant}/crm/contact/${record.id}`)
            }
          />
          <Popconfirm
            title={t('DeleteContactConfirm')}
            onConfirm={() => crmStore.deleteContact(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ───────────────────────────────────── render ─────────────────────────────────── */

  return (
    <Spin spinning={crmStore.isLoading}>
      <Card
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              navigate(`/${applicationStore.company?.Tenant}/crm/contact/new`)
            }
          >
            {t('AddContact')}
          </Button>
        }
      >
        {/* Filters */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            placeholder={t('Search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />

          <Select
            placeholder={t('Role')}
            allowClear
            style={{ width: 180 }}
            value={filterRole ?? undefined}
            onChange={(val) => setFilterRole(val ?? null)}
          >
            {crmStore.clientRoles.map((role) => (
              <Option key={role.id} value={role.id}>
                {role.name}
              </Option>
            ))}
          </Select>

          <Select
            placeholder={t('Company')}
            allowClear
            style={{ width: 200 }}
            value={filterCompany ?? undefined}
            onChange={(val) => setFilterCompany(val ?? null)}
          >
            {crmStore.externalCompanies.map((company) => (
              <Option key={company.id} value={company.id}>
                {company.name}
              </Option>
            ))}
          </Select>

          <Button onClick={resetFilters}>{t('Reset')}</Button>
        </Space>

        <Table
          dataSource={filteredContacts}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </Spin>
  );
});

export default ContactManagement;
